import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { cn } from "@/lib/utils";
import { getConsent } from "@/components/CookieConsent";

// Renders a reserved ad slot. In production with publisher IDs + consent,
// renders a real <ins class="adsbygoogle">. Gated additionally by
// /api/ads/status (kill switch + suspicious-IP detection).

const sizes = {
    leaderboard: { className: "w-full max-w-[728px] h-[90px] mx-auto", format: "horizontal" },
    sidebar: { className: "w-full max-w-[300px] h-[250px] mx-auto", format: "rectangle" },
    native: { className: "w-full h-[150px] sm:h-[200px]", format: "fluid" },
    skyscraper: { className: "w-full max-w-[300px] h-[600px] mx-auto", format: "vertical" },
    footer: { className: "w-full max-w-[970px] h-[90px] mx-auto", format: "horizontal" },
};

const PUBLISHER_ID = process.env.REACT_APP_ADSENSE_PUBLISHER_ID;
const SLOTS = {
    leaderboard: process.env.REACT_APP_ADSENSE_SLOT_LEADERBOARD,
    sidebar: process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR,
    native: process.env.REACT_APP_ADSENSE_SLOT_NATIVE,
    footer: process.env.REACT_APP_ADSENSE_SLOT_FOOTER,
    skyscraper: process.env.REACT_APP_ADSENSE_SLOT_SKYSCRAPER,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache the server-side ad-status for the whole session to avoid re-querying
// on every AdPlaceholder mount. Evaluated lazily on first render.
let _adStatusPromise = null;
function getServerAdStatus() {
    if (!_adStatusPromise) {
        _adStatusPromise = axios
            .get(`${API}/ads/status`)
            .then((r) => r.data)
            .catch(() => ({ enabled: true }));
    }
    return _adStatusPromise;
}

// Fire click beacon when user clicks inside an ad slot. Feeds the
// suspicious-IP detector on the backend.
function beaconClick() {
    axios.post(`${API}/ads/click-beacon`).catch(() => {});
}

export default function AdPlaceholder({ type = "native", className, dataTestId }) {
    const { className: sizeCls, format } = sizes[type] ?? sizes.native;
    const slot = SLOTS[type];
    const ref = useRef(null);
    const [serverEnabled, setServerEnabled] = useState(true);

    const consentOk = getConsent()?.ads ?? false;
    const envConfigured = !!PUBLISHER_ID && !!slot;
    const showRealAd = envConfigured && consentOk && serverEnabled;

    useEffect(() => {
        let active = true;
        getServerAdStatus().then((s) => {
            if (active) setServerEnabled(!!s.enabled);
        });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (!showRealAd) return;
        try {
            // eslint-disable-next-line no-unused-expressions
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch {
            /* adsbygoogle not ready yet; ThirdPartyScripts will load it */
        }
    }, [showRealAd]);

    if (showRealAd) {
        return (
            <div
                className={cn(sizeCls, className)}
                data-testid={dataTestId ?? `ad-${type}`}
                onClick={beaconClick}
            >
                <ins
                    ref={ref}
                    className="adsbygoogle block w-full h-full"
                    data-ad-client={PUBLISHER_ID}
                    data-ad-slot={slot}
                    data-ad-format={format}
                    data-full-width-responsive="true"
                />
            </div>
        );
    }

    return (
        <div
            className={cn(
                "ad-slot rounded-md border border-border/70 flex flex-col items-center justify-center",
                "text-[10px] tracking-[0.3em] uppercase text-muted-foreground select-none",
                sizeCls,
                className,
            )}
            aria-label="Advertisement"
            data-testid={dataTestId ?? `ad-${type}`}
        >
            <span className="bg-background/90 px-2 py-0.5 rounded">Advertisement</span>
        </div>
    );
}
